import React, { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Package, Clock, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface SyncStats {
  id: string;
  batch_start: string;
  batch_end: string | null;
  items_received: number;
  items_changed: number;
  items_unchanged: number;
}

export const RecentSyncBanner: React.FC = () => {
  const [syncStats, setSyncStats] = useState<SyncStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const lastFetchAtRef = useRef<number>(0);
  const inFlightRef = useRef(false);
  const scheduledRef = useRef<number | null>(null);

  const fetchSyncStats = async () => {
    if (inFlightRef.current) return;

    setIsLoading(true);
    inFlightRef.current = true;
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from("sync_statistics")
        .select("*")
        .gte("batch_start", oneHourAgo)
        .order("batch_start", { ascending: false })
        .limit(1);

      if (error) {
        console.error("Error fetching sync stats:", error);
        return;
      }

      lastFetchAtRef.current = Date.now();

      if (data && data.length > 0 && data[0].items_received > 0) {
        setSyncStats(data[0]);
      } else {
        setSyncStats(null);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncStats();

    const MIN_INTERVAL_MS = 10_000;

    const scheduleFetch = () => {
      const now = Date.now();
      const elapsed = now - lastFetchAtRef.current;
      const wait = Math.max(0, MIN_INTERVAL_MS - elapsed);

      if (scheduledRef.current) return;

      scheduledRef.current = window.setTimeout(() => {
        scheduledRef.current = null;
        fetchSyncStats();
      }, wait);
    };

    // Listen to sync_statistics changes
    const channel = supabase
      .channel("sync-stats-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sync_statistics",
        },
        () => {
          scheduleFetch();
        }
      )
      .subscribe();

    return () => {
      if (scheduledRef.current) {
        window.clearTimeout(scheduledRef.current);
        scheduledRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, []);

  if (isLoading) {
    return null;
  }

  if (!syncStats) {
    return null;
  }

  const timeAgo = formatDistanceToNow(new Date(syncStats.batch_start), {
    addSuffix: true,
    locale: nl,
  });

  return (
    <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-green-500 rounded-full p-2">
            <Package className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="font-medium text-green-800 dark:text-green-200">
              {syncStats.items_received} items ontvangen via API sync
            </p>
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <Clock className="h-3 w-3" />
              <span>{timeAgo}</span>
              <span>•</span>
              <Badge variant="outline" className="text-xs border-green-300 dark:border-green-700 text-green-700 dark:text-green-300">
                {syncStats.items_changed} gewijzigd
              </Badge>
              <Badge variant="outline" className="text-xs border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                {syncStats.items_unchanged} ongewijzigd
              </Badge>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchSyncStats}
          className="text-green-700 hover:text-green-800 hover:bg-green-100"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
